/**
 * The garbage-in guard (ROADMAP MD2). Decides whether a parcel is described well
 * enough for an attorney to actually find it at a Land Registry.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TODO-BLOCKED-INTAKE-SUFFICIENCY — needs the reviewing attorney's sign-off.
 *
 * "Sufficient" is a domain judgement about what a title searcher can work from,
 * not an engineering one, and getting it wrong is expensive in both directions:
 * too strict turns away real clients, too loose sends an attorney to a registry
 * with nothing to search for and bills the client for it.
 *
 * The rule is written out here, in one place, in terms an attorney can read and
 * correct — rather than buried across a validator — precisely so it can be
 * corrected. It is deliberately CONSERVATIVE: when the answer is unclear the
 * result is NEEDS_INFO, because asking the client one more question is cheap and
 * a wasted registry trip is not.
 *
 * The specific question for the attorney: is a current deed reference (number +
 * date + notary) genuinely enough to trace a chain, or is a survey plan
 * effectively required in practice?
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** Blocking gaps. Any one of these sends the case to NEEDS_INFO. */
export const INTAKE_GAPS = [
    /** No district: we cannot even work out which registry has jurisdiction. */
    "DISTRICT_MISSING",
    /** Neither a survey plan + lot, nor a usable deed reference. */
    "PARCEL_NOT_IDENTIFIABLE",
    /** A deed number was given but not the date and notary needed to find it. */
    "DEED_REFERENCE_INCOMPLETE",
    /** See `assessParcel` — we cannot yet identify title-registered parcels. */
    "TITLE_SYSTEM_NOT_YET_SUPPORTED",
];
/**
 * Worth asking for, but not worth refusing over. These make the search faster or
 * cheaper; their absence does not make it impossible.
 */
export const INTAKE_ADVISORIES = [
    "EXTENT_MISSING",
    "DS_DIVISION_MISSING",
    "VILLAGE_MISSING",
    "NO_PRIOR_DEEDS",
    "LAND_SYSTEM_UNKNOWN",
];
const present = (value) => typeof value === "string" ? value.trim().length > 0 : value !== null && value !== undefined;
/**
 * A parcel is identifiable if a searcher could find it from what we hold. Two
 * routes, either sufficient:
 *
 *  1. A survey plan number AND a lot number. The strongest identifier — a plan
 *     locates the land geometrically and the lot picks out the parcel.
 *  2. A current deed reference: number AND date AND notary. All three, because a
 *     deed number alone does not locate a folio — deeds are indexed by notary and
 *     year, and "Deed No. 1234" without them matches thousands.
 */
export function assessParcel(parcel) {
    const gaps = [];
    const advisories = [];
    if (!present(parcel.district)) {
        gaps.push("DISTRICT_MISSING");
    }
    // Sri Lanka registers land under two parallel systems and the workflow must
    // handle both (CLAUDE.md §5). It does not yet. The parcel identification block
    // in ARCHITECTURE §4 is entirely deed-shaped — current deed, prior deeds,
    // notary — and none of that identifies a parcel registered under the
    // Registration of Title Act, which is identified by its cadastral map and
    // title certificate instead.
    //
    // Rather than guess at fields for a system we cannot verify the details of, or
    // silently accept a title parcel we cannot search, we refuse it and say why.
    // An honest bounce is a first-class outcome (CLAUDE.md non-negotiable 1); a
    // case accepted on identification we know is wrong is not.
    //
    // TODO-BLOCKED-TITLE-SYSTEM: needs the parcel block extended with the real
    // Bim Saviya identifiers, confirmed with the attorney, before MD2's gate can
    // be said to hold for title-registered land.
    if (parcel.landSystem === "title") {
        gaps.push("TITLE_SYSTEM_NOT_YET_SUPPORTED");
    }
    const hasSurveyIdentification = present(parcel.surveyPlanNo) && present(parcel.lotNo);
    const hasAnyDeedReference = present(parcel.currentDeedNo);
    const hasCompleteDeedReference = hasAnyDeedReference && present(parcel.currentDeedDate) && present(parcel.currentDeedNotary);
    if (!hasSurveyIdentification && !hasCompleteDeedReference) {
        // Distinguish "you gave us nothing" from "you nearly gave us enough", because
        // the client can act on the second and the first needs a different question.
        if (hasAnyDeedReference) {
            gaps.push("DEED_REFERENCE_INCOMPLETE");
        }
        else {
            gaps.push("PARCEL_NOT_IDENTIFIABLE");
        }
    }
    if (!present(parcel.extent))
        advisories.push("EXTENT_MISSING");
    if (!present(parcel.dsDivision))
        advisories.push("DS_DIVISION_MISSING");
    if (!present(parcel.village))
        advisories.push("VILLAGE_MISSING");
    if (!parcel.priorDeedRefs || parcel.priorDeedRefs.length === 0)
        advisories.push("NO_PRIOR_DEEDS");
    // Not knowing which system the land is under is normal — most clients do not.
    // The attorney determines it. It is not a reason to refuse the case.
    if (!present(parcel.landSystem))
        advisories.push("LAND_SYSTEM_UNKNOWN");
    return { sufficient: gaps.length === 0, gaps, advisories };
}
/**
 * What the client is told, per gap.
 *
 * Every line asks for information. None states a fact about the client's land,
 * however tempting the shortcut — the platform coordinates and never concludes
 * (CLAUDE.md non-negotiables 1 and 2), and that applies to a form hint as much as
 * to a report.
 *
 * The title one is the trap, and the first cut fell in it. It said "This parcel
 * appears to be registered under the title registration system", which reads as
 * the platform having determined something about their land. It had determined
 * nothing: the client ticked a box. Which system a parcel is under is a question
 * the attorney answers from the registry, and most clients genuinely do not know.
 * So the copy attributes the claim to the person who made it.
 */
export const GAP_GUIDANCE = {
    DISTRICT_MISSING: "We need the district the land is in, so we can identify the correct Land Registry.",
    PARCEL_NOT_IDENTIFIABLE: "We need either the survey plan number and lot number, or the current deed's number, date and the notary who attested it. Without one of those, the land cannot be located at the registry.",
    DEED_REFERENCE_INCOMPLETE: "We have the deed number but also need its date and the notary who attested it — deeds are indexed by notary and year, so the number alone is not enough to find it.",
    TITLE_SYSTEM_NOT_YET_SUPPORTED: "You told us this land is registered under the title registration system (Bim Saviya). We are not able to take those requests yet — please contact us directly. If you are not sure which system applies, leave that field blank and we will find out for you.",
};
//# sourceMappingURL=intake.js.map